import { useEffect, useRef } from 'react';

import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { python } from '@codemirror/lang-python';
import { HighlightStyle, indentUnit, syntaxHighlighting, syntaxTree } from '@codemirror/language';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorState, StateField } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { tags } from '@lezer/highlight';
import styled from 'styled-components';

import { FormError } from '@components/form-error';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

const highlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: theme.color.elementPurpleInvert },
  { tag: tags.number, color: theme.color.elementOrange100 },
  { tag: tags.string, color: theme.color.elementGreen100 },
  { tag: tags.comment, color: theme.color.elementPurple50 },
  { tag: tags.variableName, color: theme.color.elementBlue200 },
  { tag: tags.function(tags.variableName), color: theme.color.elementBlue100 },
  { tag: tags.operator, color: theme.color.elementRed100 },
  { tag: tags.definition(tags.variableName), color: theme.color.elementOrange100 },
]);

export type PythonEditorProps = {
  defaultValue?: string;
  label: string;
  onChange: (value: string) => void;
  onCodeErrorChange?: (hasError: boolean) => void;
  error?: string;
  isDisabled?: boolean;
};

export const PythonEditor = ({
  defaultValue = '',
  onChange,
  onCodeErrorChange,
  label,
  error = undefined,
  isDisabled = false,
}: PythonEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current) {
      const changeHandler = StateField.define<string>({
        create: () => defaultValue,
        update(value, transaction) {
          return transaction.docChanged ? transaction.state.doc.toString() : value;
        },
      });

      const pythonLinter = linter((view) => {
        const diagnostics: any = [];
        const tree = syntaxTree(view.state);
        let hasSyntaxError = false;
        tree.iterate({
          enter: (node) => {
            if (node.type.isError) {
              hasSyntaxError = true;
              diagnostics.push({
                from: node.from,
                to: node.to,
                severity: 'error',
                message: 'Syntax error',
              });
            }
          },
        });
        onCodeErrorChange?.(hasSyntaxError);
        return diagnostics;
      });

      const view = new EditorView({
        doc: defaultValue,
        extensions: [
          EditorState.readOnly.of(isDisabled),
          history(),
          keymap.of([...defaultKeymap, indentWithTab, ...historyKeymap]),
          python(),
          syntaxHighlighting(highlightStyle),
          indentUnit.of('  '),
          lineNumbers(),
          pythonLinter,
          lintGutter(),
          changeHandler,
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              const currentValue = update.state.field(changeHandler);
              onChange?.(currentValue);
            }
          }),
        ],
        parent: editorRef.current,
      });

      return () => {
        view.destroy();
      };
    }
  }, [defaultValue, onChange, onCodeErrorChange, isDisabled]);

  return (
    <div>
      <Container>
        <Stack orientation="horizontal" justify="space-between">
          <Text font="semibold14" color="textPrimary" text={label} />
          <Text font="bold14" text="🐍 Python" color="textHelper" />
        </Stack>
      </Container>
      <StyledEditor ref={editorRef} data-error={Boolean(error)} />
      <FormError text={error} />
    </div>
  );
};

const Container = styled.div({
  borderRadius: `${theme.space[2]} ${theme.space[2]} 0 0`,
  borderWidth: `${pxToRem(1)} ${pxToRem(1)} 0 ${pxToRem(1)}`,
  borderStyle: 'solid',
  borderColor: theme.color.border20,
  backgroundColor: theme.form.bg.color,
  padding: `${theme.space[2]} ${theme.space[3]}`,
});

const StyledEditor = styled.div({
  '.cm-content': {
    caretColor: 'auto',
  },
  '.cm-editor': {
    overflow: 'auto',
    maxHeight: pxToRem(384),
    margin: 0,
    border: `${pxToRem(1)} solid ${theme.color.border20}`,
    borderRadius: `0 0 ${theme.space[2]} ${theme.space[2]}`,
    padding: `${theme.space[2]} ${theme.space[3]}`,
    color: theme.form.value.color,
    backgroundColor: theme.form.bg.color,
    ':hover': {
      backgroundColor: theme.form.bg.colorHover,
    },
  },
  '.cm-lintPoint:after': {
    borderBottom: `${pxToRem(4)} solid ${theme.color.elementRed100}`,
  },
  '.cm-gutters': {
    backgroundColor: 'inherit',
    color: theme.color.textHelper,
    border: 'none',
  },
  '.cm-editor.cm-focused': {
    backgroundColor: theme.form.bg.colorFocus,
    border: `${pxToRem(1)} solid ${theme.form.border.colorFocus}`,
    outline: 'none',
  },
  '.cm-editor.cm-focused .cm-gutters': {
    backgroundColor: theme.form.bg.colorFocus,
  },
  '&[data-error="true"] .cm-editor': {
    borderColor: theme.color.elementRed100,
  },
  '.cm-tooltip': {
    border: 'none',
    borderRadius: '100%',
  },
  '.cm-diagnostic': {
    outline: 'none',
    border: 'none',
    borderRadius: theme.space[2],
    backgroundColor: theme.color.elementRed100,
    color: theme.color.white100,
    padding: theme.space[4],
  },
  '.cm-gutterElement': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '.cm-lint-marker-error': {
    content: 'none',
    borderRadius: '100%',
    background: theme.color.elementRed100,
    height: pxToRem(8),
    width: pxToRem(8),
    ':hover': {
      background: theme.color.elementRed200,
    },
  },
});
